import { z } from 'zod'
import {
  MARKET_KEYS, PAIR_MARKETS, SLIP_TYPES, bonusPct, countColumns, currentOdds, isOutrightMarket, legKey, sameOutcome, slipPotential,
  type BonusRule, type MarketsResponse, type Outcome, type PlaceSlip, type SelectionRef, type Slip, type SlipStatus, type SlipType, type ZambeBetQuotesResponse,
} from '@brochure/shared'

export type DraftSelection = Outcome & { odds: number; /** Solo per mostrarla: il nome del giocatore. */ subjectName?: string }
/** `matchId` null: un antepost (l'evento è il suo mercato, vedi `legKey`), con `home`/`away` vuoti. */
export type DraftLeg = { matchId: string | null; home: string; away: string; banker: boolean; selections: DraftSelection[] }
export type DraftSlip = { type: SlipType; systemSizes: number[]; stakePerColumn: number; legs: DraftLeg[] }

export const EMPTY_SLIP: DraftSlip = { type: 'single', systemSizes: [], stakePerColumn: 5, legs: [] }

export const selectionCount = (slip: DraftSlip): number => slip.legs.reduce((n, leg) => n + leg.selections.length, 0)

/** Singola con un esito; multipla con un esito per partita su più partite; sistema con almeno due esiti (due colonne). */
export function allowedTypes(slip: DraftSlip): SlipType[] {
  const count = selectionCount(slip)
  return [
    ...(count === 1 ? (['single'] as const) : []),
    ...(slip.legs.length >= 2 && slip.legs.every((l) => l.selections.length === 1) ? (['multiple'] as const) : []),
    ...(count >= 2 ? (['system'] as const) : []),
  ]
}

/** Rimette in ordine dopo ogni modifica: niente eventi vuoti, tipo ammesso, fisse solo nel sistema, ordini validi. */
export function normalizeSlip(slip: DraftSlip): DraftSlip {
  const nonEmpty = slip.legs.filter((l) => l.selections.length > 0)
  const allowed = allowedTypes({ ...slip, legs: nonEmpty })
  const type = allowed.includes(slip.type) ? slip.type : (allowed[0] ?? 'single')
  const keepBankers = type === 'system' && !nonEmpty.every((l) => l.banker)
  const legs = nonEmpty.map((l, i) => {
    // Un sistema ha sempre almeno un evento libero: se restano solo fisse, l'ultima torna libera.
    const banker = type === 'system' && l.banker && (keepBankers || i < nonEmpty.length - 1)
    return banker === l.banker ? l : { ...l, banker }
  })
  const free = legs.filter((l) => !l.banker).length
  const kept = slip.systemSizes.filter((k) => k >= 1 && k <= free)
  const systemSizes = type !== 'system' ? [] : kept.length > 0 ? kept : free > 0 ? [free] : []
  return { ...slip, type, legs, systemSizes }
}

const opposite = (a: Outcome, b: Outcome) =>
  PAIR_MARKETS.has(a.market) && a.market === b.market && (a.line ?? null) === (b.line ?? null) && (a.subjectId ?? null) === (b.subjectId ?? null) && a.selection !== b.selection

/** Tocca un esito: lo aggiunge (sostituendo il suo opposto, es. "2" dopo "1") o lo toglie se c'era già. Un antepost va nell'evento del suo mercato. */
export function toggleSelection(slip: DraftSlip, leg: { matchId: string | null; home: string; away: string }, selection: DraftSelection): DraftSlip {
  const key = legKey({ matchId: leg.matchId, selections: [selection] })
  const current = slip.legs.find((l) => legKey(l) === key)
  const toggle = (selections: DraftSelection[]) =>
    selections.some((s) => sameOutcome(s, selection)) ? selections.filter((s) => !sameOutcome(s, selection)) : [...selections.filter((s) => !opposite(s, selection)), selection]
  const legs = current
    ? slip.legs.map((l) => (legKey(l) === key ? { ...l, selections: toggle(l.selections) } : l))
    : [...slip.legs, { ...leg, banker: false, selections: [selection] }]
  return normalizeSlip({ ...slip, legs })
}

export const hasSelection = (slip: DraftSlip, matchId: string | null, outcome: Outcome): boolean => {
  const key = legKey({ matchId, selections: [outcome] })
  return slip.legs.some((l) => legKey(l) === key && l.selections.some((s) => sameOutcome(s, outcome)))
}

export const removeSelection = (slip: DraftSlip, ref: SelectionRef): DraftSlip =>
  normalizeSlip({ ...slip, legs: slip.legs.map((l, i) => (i === ref.leg ? { ...l, selections: l.selections.filter((_, j) => j !== ref.selection) } : l)) })

/** Cambia tipo se ammesso; passando al sistema gli ordini ripartono da "tutti gli eventi". */
export const setType = (slip: DraftSlip, type: SlipType): DraftSlip =>
  allowedTypes(slip).includes(type) ? normalizeSlip({ ...slip, type, systemSizes: [] }) : slip

/** Fissa sì/no (solo sistema); almeno un evento resta libero, altrimenti il sistema non ha colonne. */
export function toggleBanker(slip: DraftSlip, legIndex: number): DraftSlip {
  if (slip.type !== 'system') return slip
  const legs = slip.legs.map((l, i) => (i === legIndex ? { ...l, banker: !l.banker } : l))
  return legs.every((l) => l.banker) ? slip : normalizeSlip({ ...slip, legs })
}

/** Aggiunge o toglie un ordine; l'ultimo ordine non si toglie. */
export function toggleSystemSize(slip: DraftSlip, k: number): DraftSlip {
  const sizes = slip.systemSizes.includes(k) ? slip.systemSizes.filter((s) => s !== k) : [...slip.systemSizes, k].sort((a, b) => a - b)
  return sizes.length > 0 ? normalizeSlip({ ...slip, systemSizes: sizes }) : slip
}

export type SlipQuoteState = { current: (number | null)[][]; closed: SelectionRef[]; changed: SelectionRef[] }

/** Confronta le quote accettate con quelle correnti; finché le quote non arrivano valgono quelle accettate. */
export function slipQuoteState(slip: DraftSlip, quotes: ZambeBetQuotesResponse | undefined): SlipQuoteState {
  const closed: SelectionRef[] = []
  const changed: SelectionRef[] = []
  const current = slip.legs.map((leg, l) =>
    leg.selections.map((selection, s) => {
      if (!quotes) return selection.odds
      const odds = currentOdds(quotes[legKey(leg)]?.markets, selection)
      if (odds === null) closed.push({ leg: l, selection: s })
      else if (odds !== selection.odds) changed.push({ leg: l, selection: s })
      return odds
    }),
  )
  return { current, closed, changed }
}

/** "Accetta le nuove quote": le quote cambiate diventano quelle correnti. */
export const acceptOdds = (slip: DraftSlip, state: SlipQuoteState): DraftSlip => ({
  ...slip,
  legs: slip.legs.map((leg, l) => ({ ...leg, selections: leg.selections.map((s, j) => ({ ...s, odds: state.current[l]?.[j] ?? s.odds })) })),
})

/** Toglie in un colpo gli esiti non più offerti. */
export const removeClosed = (slip: DraftSlip, state: SlipQuoteState): DraftSlip =>
  normalizeSlip({ ...slip, legs: slip.legs.map((leg, l) => ({ ...leg, selections: leg.selections.filter((_, s) => !state.closed.some((c) => c.leg === l && c.selection === s)) })) })

export function toPlaceSlip(slip: DraftSlip): PlaceSlip {
  return {
    type: slip.type,
    ...(slip.type === 'system' && { systemSizes: slip.systemSizes }),
    stakePerColumn: slip.stakePerColumn,
    legs: slip.legs.map((l) => ({
      matchId: l.matchId,
      banker: l.banker,
      selections: l.selections.map(({ market, selection, line, subjectId, odds }) => ({ market, selection, ...(line !== undefined && { line }), ...(subjectId !== undefined && { subjectId }), odds })),
    })),
  }
}

export type SlipLimitsView = { bonus: BonusRule[]; maxPayout: number | null; maxBet: number | null; maxLegs: number; maxColumns: number; balance: number }
/** Limiti della schedina dal torneo (risposta dei mercati) e dal saldo dell'ospite. */
export const slipLimits = (tournament: MarketsResponse['tournament'], balance: number): SlipLimitsView => ({
  bonus: tournament.zambebetBonus, maxPayout: tournament.zambebetMaxPayout, maxBet: tournament.maxBet,
  maxLegs: tournament.zambebetMaxLegs, maxColumns: tournament.zambebetMaxColumns, balance,
})
export type SlipWarning = 'tooManyLegs' | 'tooManyColumns' | 'overMaxBet' | 'insufficientBalance'
export type SlipSummary = { columns: number; totalStake: number; totalOdds: number; bonusPct: number; potential: { min: number; max: number }; warnings: SlipWarning[] }

/** Riepilogo della schedina in bozza, con le stesse funzioni che usa il server. `totalOdds` ha senso per singola e multipla. */
export function slipSummary(slip: DraftSlip, limits: SlipLimitsView): SlipSummary {
  const columns = countColumns(slip)
  const totalStake = columns * slip.stakePerColumn
  const totalOdds = Math.round(slip.legs.reduce((product, l) => product * (l.selections[0]?.odds ?? 1), 1) * 100) / 100
  // Oltre il tetto la schedina non si può giocare: niente enumerazione (con molte colonne bloccherebbe la pagina a ogni render).
  const potential = columns > limits.maxColumns ? { min: 0, max: 0 } : slipPotential({
    type: slip.type, systemSizes: slip.systemSizes, stakePerColumn: slip.stakePerColumn, bonus: limits.bonus, maxPayout: limits.maxPayout,
    legs: slip.legs.map((l) => ({ banker: l.banker, selections: l.selections.map((s) => ({ odds: s.odds, status: 'open' as const })) })),
  })
  const warnings: SlipWarning[] = [
    ...(slip.legs.length > limits.maxLegs ? (['tooManyLegs'] as const) : []),
    ...(columns > limits.maxColumns ? (['tooManyColumns'] as const) : []),
    ...(limits.maxBet !== null && totalStake > limits.maxBet ? (['overMaxBet'] as const) : []),
    ...(totalStake > limits.balance ? (['insufficientBalance'] as const) : []),
  ]
  return { columns, totalStake, totalOdds, bonusPct: slip.type === 'system' ? 0 : bonusPct(limits.bonus, slip.legs.length), potential, warnings }
}

/** Dopo una giocata riuscita la bozza si svuota solo se è ancora quella inviata; se nel frattempo è cambiata, resta. */
export const draftAfterPlaced = (submitted: DraftSlip, current: DraftSlip): DraftSlip =>
  JSON.stringify(submitted) === JSON.stringify(current) ? EMPTY_SLIP : current

/**
 * "Rigioca": la bozza ricostruita da una schedina giocata (di solito annullata), con le quote di allora. Lo sheet le
 * confronta con le correnti e mostra "vecchia → nuova". Un antepost prende i nomi dai `subjects` dell'evento; gli eventi
 * senza partita che non sono antepost non si possono rigiocare: saltati.
 */
export function draftFromSlip(slip: Slip): DraftSlip {
  const legs = slip.legs.flatMap((leg): DraftLeg[] => {
    const { match } = leg
    const isOutright = !match && leg.selections.length > 0 && leg.selections.every((s) => isOutrightMarket(s.market))
    if (!match && !isOutright) return []
    const names = match ? match.players : (leg.subjects ?? [])
    const selections = leg.selections.map((s): DraftSelection => {
      const subjectName = s.subjectId === null ? undefined : names.find((p) => p.id === s.subjectId)?.name
      return {
        market: s.market, selection: s.selection,
        ...(s.line !== null && { line: s.line }),
        ...(s.subjectId !== null && { subjectId: s.subjectId }),
        ...(subjectName !== undefined && { subjectName }),
        odds: s.odds,
      }
    })
    return [{ matchId: match?.id ?? null, home: match?.homeTeam.name ?? '', away: match?.awayTeam.name ?? '', banker: leg.banker, selections }]
  })
  return normalizeSlip({ type: slip.type, systemSizes: slip.systemSizes ?? [], stakePerColumn: slip.stakePerColumn, legs })
}

/** Nessun esito della schedina è più in offerta (es. partita al pareggio o conclusa): non c'è niente da rigiocare. Falso finché le quote non arrivano. */
export function nothingPlayable(slip: Slip, quotes: ZambeBetQuotesResponse | undefined): boolean {
  if (!quotes) return false
  const draft = draftFromSlip(slip)
  return slipQuoteState(draft, quotes).closed.length === draft.legs.reduce((n, leg) => n + leg.selections.length, 0)
}

/**
 * "Rigioca" con una bozza già iniziata: si aggiungono gli esiti della schedina annullata che la bozza non ha, come
 * tanti tocchi (l'opposto già in bozza, es. "2" contro "1", viene sostituito). Tipo e puntata restano quelli della
 * bozza; se è vuota, vale la schedina in arrivo così com'è.
 */
export function mergeDrafts(current: DraftSlip, incoming: DraftSlip): DraftSlip {
  if (selectionCount(current) === 0) return incoming
  return incoming.legs.reduce(
    (slip, leg) => leg.selections.reduce((acc, s) => (hasSelection(acc, leg.matchId, s) ? acc : toggleSelection(acc, leg, s)), slip),
    current,
  )
}

/** Durata dell'attesa di una schedina live (da `placedAt` ad `acceptAt`); `fallbackMs` se le date mancano o non tornano. */
export function pendingDurationMs(slip: Pick<Slip, 'placedAt' | 'acceptAt'>, fallbackMs: number): number {
  const duration = slip.acceptAt ? Date.parse(slip.acceptAt) - Date.parse(slip.placedAt) : Number.NaN
  return Number.isFinite(duration) && duration > 0 ? duration : fallbackMs
}

/**
 * Millisecondi che mancano alla conferma di una schedina live, con l'orologio del server: `serverNow` è l'ora del
 * server letta a `fetchedAtClient`, a cui si aggiunge il tempo passato da allora sul client. Sempre in `[0, delayMs]`.
 */
export function pendingRemainingMs(acceptAt: string, serverNow: string, fetchedAtClient: number, nowClient: number, delayMs: number): number {
  const remaining = Date.parse(acceptAt) - (Date.parse(serverNow) + (nowClient - fetchedAtClient))
  return Number.isNaN(remaining) ? 0 : Math.min(delayMs, Math.max(0, remaining))
}

/** Filtro delle "Schedine": in attesa conta come aperta, annullata come conclusa. */
export const slipFilterBucket = (status: SlipStatus): 'open' | 'closed' => (status === 'pending' || status === 'open' ? 'open' : 'closed')

/** Le schedine che erano in attesa (`previousIds`) e ora non lo sono più: confermate o annullate. */
export const resolvedPending = (previousIds: ReadonlySet<string>, slips: readonly Slip[]): Slip[] =>
  slips.filter((s) => previousIds.has(s.id) && s.status !== 'pending')

const StoredSlipSchema = z.object({
  type: z.enum(SLIP_TYPES),
  systemSizes: z.array(z.number().int()),
  stakePerColumn: z.number().int().min(1),
  legs: z.array(
    z.object({
      matchId: z.string().nullable(),
      home: z.string(),
      away: z.string(),
      banker: z.boolean(),
      selections: z.array(z.object({ market: z.enum(MARKET_KEYS), selection: z.string(), line: z.number().optional(), subjectId: z.string().optional(), subjectName: z.string().optional(), odds: z.number() })),
    }),
  ),
})

const storageKey = (tournamentId: string) => `zambebet:slip:${tournamentId}`

/** Schedina salvata per torneo; qualsiasi cosa illeggibile (vecchia versione, modalità privata) → schedina vuota. */
export function readSlip(storage: Pick<Storage, 'getItem'> | null, tournamentId: string): DraftSlip {
  try {
    const raw = storage?.getItem(storageKey(tournamentId))
    const parsed = raw ? StoredSlipSchema.safeParse(JSON.parse(raw)) : null
    return parsed?.success ? normalizeSlip(parsed.data) : EMPTY_SLIP
  } catch {
    return EMPTY_SLIP
  }
}

export function writeSlip(storage: Pick<Storage, 'setItem' | 'removeItem'> | null, tournamentId: string, slip: DraftSlip): void {
  try {
    if (slip.legs.length === 0) storage?.removeItem(storageKey(tournamentId))
    else storage?.setItem(storageKey(tournamentId), JSON.stringify(slip))
  } catch {
    /* modalità privata: la schedina resta solo in memoria */
  }
}
